import React, { useState } from 'react';
import NavigationStepper, {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../../../Components/Stepper/NavigationStepper';
import LaboratoryActivationResultsDistribution, {
	LaboratoryActivationResultsDistributionValues,
} from './Steps/LaboratoryActivationResultsDistribution';
import LaboratoryActivationCompanyData, {
	LaboratoryActivationCompanyDataValues,
} from './Steps/LaboratoryActivationCompanyData';
import LaboratoryActivationCollectingMethod, {
	LaboratoryActivationCollectingMethodValues,
} from './Steps/LaboratoryActivationCollectingMethod';
import LaboratoryActivationTransferConsent from './Steps/LaboratoryActivationTransferConsent';
import {
	Laboratory,
	LaboratoryWorkplace,
	useCreateLaboratoryWorkplaceMutation,
	useUpdateLaboratoryWorkplaceMutation,
} from '../../../../../graphql/graphql';
import useTranslation from '../../../../../Hooks/useTranslation';
import useApiClient from '../../../../Base/Hooks/useApiClient';
import useNotification from '../../../../../Hooks/useNotification.hook';
import ConfirmationModal from '../../../../Base/Components/Confirmation/ConfirmationModal';
import Button from '../../../../../Components/Button/Button';
import { Theme } from '../../../../../Enums/Theme.enum';

interface ActivationData {
	companyData?: LaboratoryActivationCompanyDataValues;
	resultsDistribution?: LaboratoryActivationResultsDistributionValues;
	collectingMethod?: LaboratoryActivationCollectingMethodValues;
}

interface Props {
	workplaceId: string;
	onStepChange: (stepName: string) => void;
	laboratory: Laboratory;
	onActivationSuccess: () => void;
	workplaceLaboratory?: LaboratoryWorkplace;
}

const LaboratoryActivation: React.FC<Props> = ({
	workplaceId,
	onStepChange,
	laboratory,
	onActivationSuccess,
	workplaceLaboratory,
}) => {
	const { t } = useTranslation();
	const { displayGQLError } = useApiClient();
	const { showNotificationSuccess } = useNotification();
	const [createLaboratoryWorkplace] = useCreateLaboratoryWorkplaceMutation();
	const [updateLaboratoryWorkplace] = useUpdateLaboratoryWorkplaceMutation();
	const [isRejectConfirmationOpen, setIsRejectConfirmationOpen] =
		useState(false);

	const [data, setData] = useState<ActivationData>({});

	const handleOnCompanyDataSuccess = (
		params: NavigationStepperComponentParams,
		values: LaboratoryActivationCompanyDataValues,
	) => {
		setData(prev => ({ ...prev, companyData: values }));
		onStepChange('component.laboratoryActivationResultsDistribution.title');
		params.setActiveStepPath('resultsDistribution');
	};

	const handleDistributionSuccess = (
		params: NavigationStepperComponentParams,
		values: LaboratoryActivationResultsDistributionValues,
	) => {
		setData(prev => ({ ...prev, resultsDistribution: values }));
		onStepChange('component.laboratoryActivationCollectingMethod.title');
		params.setActiveStepPath('collectingMethod');
	};

	const handleCollectingSuccess = (
		params: NavigationStepperComponentParams,
		values: LaboratoryActivationCollectingMethodValues,
	) => {
		setData(prev => ({ ...prev, collectingMethod: values }));
		onStepChange(
			`${t('component.laboratoryActivationTransferConsent.title')} ${
				laboratory.name
			}`,
		);
		params.setActiveStepPath('transferConsent');
	};

	const handleCreate = async () => {
		const result = await createLaboratoryWorkplace({
			input: { laboratoryId: laboratory.id, data, workplaceId },
		});
		displayGQLError(result);
		if ('data' in result) {
			showNotificationSuccess({
				content: t('component.laboratorySetup.laboratoryAllowed'),
			});
			onActivationSuccess();
		}
	};

	const handleUpdate = async () => {
		if (!workplaceLaboratory) return;
		const result = await updateLaboratoryWorkplace({
			where: { id: workplaceLaboratory.id },
			input: { data },
		});
		displayGQLError(result);
		if ('data' in result) {
			showNotificationSuccess({
				content: t('component.laboratorySetup.laboratoryAllowed'),
			});
			onActivationSuccess();
		}
	};

	const handleAcceptConsent = async () => {
		if (workplaceLaboratory) {
			await handleUpdate();
		} else {
			await handleCreate();
		}
	};

	const handleRejectConsent = () => {
		onActivationSuccess();
	};

	const steps: NavigationStepperItemType[] = [
		{
			name: '',
			key: 'companyData',
			component: (params: NavigationStepperComponentParams) => (
				<LaboratoryActivationCompanyData
					workplaceId={workplaceId}
					onSuccess={values => handleOnCompanyDataSuccess(params, values)}
				/>
			),
		},
		{
			name: '',
			key: 'resultsDistribution',
			component: (params: NavigationStepperComponentParams) => (
				<LaboratoryActivationResultsDistribution
					onSuccess={values => handleDistributionSuccess(params, values)}
				/>
			),
		},
		{
			name: '',
			key: 'collectingMethod',
			component: (params: NavigationStepperComponentParams) => (
				<LaboratoryActivationCollectingMethod
					onSuccess={values => handleCollectingSuccess(params, values)}
				/>
			),
		},
		{
			name: '',
			key: 'transferConsent',
			component: (params: NavigationStepperComponentParams) => (
				<LaboratoryActivationTransferConsent
					onAcceptConsent={handleAcceptConsent}
					onRejectConsent={() => setIsRejectConfirmationOpen(true)}
					laboratory={laboratory}
				/>
			),
		},
	];

	return (
		<>
			<NavigationStepper
				steps={steps}
				initialStepPath='companyData'
				showStepNavigation={false}
				canSkip
			/>
			{isRejectConfirmationOpen && (
				<ConfirmationModal
					isOpen
					title='component.laboratoryActivation.rejectConfirmation.title'
					text='component.laboratoryActivation.rejectConfirmation.text'
					onClose={() => setIsRejectConfirmationOpen(false)}>
					<Button
						theme={Theme.GREEN}
						onClick={handleRejectConsent}
						title='component.laboratoryActivation.rejectConfirmation.button'
					/>
				</ConfirmationModal>
			)}
		</>
	);
};

export default LaboratoryActivation;
